<script lang="ts">
  import { onDestroy } from 'svelte'
  import { fetchTranslation, getCachedTranslation } from '$lib/manga-translate-cache'
  import { fetchEnhancement, getCachedEnhancement } from '$lib/manga-enhance-cache'
  import { Spinner } from '$lib/components/ui/spinner/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import LanguagesIcon from '@lucide/svelte/icons/languages'
  import SparklesIcon from '@lucide/svelte/icons/sparkles'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'

  interface Props {
    server: any
    bookId: string | undefined
    pageNum: number
    baseUrl: string | undefined
    baseUrlSuffix?: string
    translateEnabled?: boolean
    /** Bump to force-retranslate this page once (0 = idle). */
    retranslateToken?: number
    onRetranslateDone?: (pageNum: number, token: number) => void
    enhanceEnabled?: boolean
    width?: number
    paddingStyle?: string
    onload?: (e: Event) => void
    onerror?: () => void
  }

  let {
    server,
    bookId,
    pageNum,
    baseUrl,
    baseUrlSuffix = '',
    translateEnabled = false,
    retranslateToken = 0,
    onRetranslateDone,
    enhanceEnabled = false,
    width,
    paddingStyle = '',
    onload,
    onerror,
  }: Props = $props()

  let rootEl = $state<HTMLElement | null>(null)
  let isNearViewport = $state(false)
  let translatedSrc = $state<string | null>(null)
  let enhancedSrc = $state<string | null>(null)
  let translating = $state(false)
  let enhancing = $state(false)
  let translateError = $state(false)
  let loadedTranslateKey = $state<string | null>(null)
  let loadedEnhanceKey = $state<string | null>(null)
  let lastHandledRetranslateToken = $state(0)

  let translateAbort: AbortController | null = null
  let enhanceAbort: AbortController | null = null
  let observer: IntersectionObserver | null = null

  const TRANSLATE_ESTIMATE_SEC = 25
  let translateRemainingSec = $state(TRANSLATE_ESTIMATE_SEC)
  let translateEstimateExceeded = $state(false)
  let estimateTimer: ReturnType<typeof setInterval> | null = null

  const estimateCountdownPct = $derived(
    Math.max(0, (translateRemainingSec / TRANSLATE_ESTIMATE_SEC) * 100),
  )

  function stopEstimateTimer() {
    if (estimateTimer) {
      clearInterval(estimateTimer)
      estimateTimer = null
    }
    translateRemainingSec = TRANSLATE_ESTIMATE_SEC
    translateEstimateExceeded = false
  }

  function startEstimateTimer() {
    stopEstimateTimer()
    const startedAt = Date.now()
    estimateTimer = setInterval(() => {
      const elapsedSec = (Date.now() - startedAt) / 1000
      if (elapsedSec >= TRANSLATE_ESTIMATE_SEC) {
        translateEstimateExceeded = true
        translateRemainingSec = 0
        return
      }
      translateRemainingSec = Math.max(1, Math.ceil(TRANSLATE_ESTIMATE_SEC - elapsedSec))
    }, 200)
  }

  const originalSrc = $derived(
    bookId
      ? `${baseUrl}/api/v1/read/${bookId}?num=${pageNum}${baseUrlSuffix}`
      : '',
  )

  const translateKey = $derived(bookId ? `${bookId}:${pageNum}:t` : null)
  const enhanceKey = $derived(bookId ? `${bookId}:${pageNum}:e` : null)

  /** Prefer translation overlay; else enhanced; else original. */
  const displaySrc = $derived(translatedSrc ?? enhancedSrc ?? originalSrc)

  function revokeTranslatedSrc() {
    if (translatedSrc) {
      URL.revokeObjectURL(translatedSrc)
      translatedSrc = null
    }
  }

  function revokeEnhancedSrc() {
    if (enhancedSrc) {
      URL.revokeObjectURL(enhancedSrc)
      enhancedSrc = null
    }
  }

  function cancelTranslateFetch() {
    translateAbort?.abort()
    translateAbort = null
  }

  function cancelEnhanceFetch() {
    enhanceAbort?.abort()
    enhanceAbort = null
  }

  function applyTranslateBlob(blob: Blob) {
    revokeTranslatedSrc()
    translatedSrc = URL.createObjectURL(blob)
    loadedTranslateKey = translateKey
    translateError = false
  }

  function applyEnhanceBlob(blob: Blob) {
    revokeEnhancedSrc()
    enhancedSrc = URL.createObjectURL(blob)
    loadedEnhanceKey = enhanceKey
  }

  async function loadTranslation(force = false) {
    if (!translateEnabled || !bookId || !server || !translateKey) {
      cancelTranslateFetch()
      if (!translateEnabled) {
        revokeTranslatedSrc()
        loadedTranslateKey = null
      }
      translating = false
      translateError = false
      return
    }

    // Normal loads wait until near viewport; force retranslate runs immediately.
    if (!force && !isNearViewport) {
      cancelTranslateFetch()
      translating = false
      return
    }

    if (!force && loadedTranslateKey === translateKey && translatedSrc) {
      return
    }

    if (!force) {
      const cached = getCachedTranslation(bookId, pageNum, false)
      if (cached) {
        applyTranslateBlob(cached)
        translating = false
        return
      }
    }

    cancelTranslateFetch()
    translating = true
    translateError = false

    const controller = new AbortController()
    translateAbort = controller

    try {
      const blob = await fetchTranslation(
        server,
        bookId,
        pageNum,
        force,
        controller.signal,
      )
      if (controller.signal.aborted) return
      if (!blob) {
        translateError = true
        return
      }
      applyTranslateBlob(blob)
    } catch (err) {
      if ((err as Error)?.name === 'CanceledError' || (err as Error)?.name === 'AbortError') {
        return
      }
      translateError = true
    } finally {
      if (translateAbort === controller) {
        translating = false
        translateAbort = null
      }
    }
  }

  async function loadEnhancement() {
    // Skip while translating — MIT has its own upscale path.
    if (
      !enhanceEnabled ||
      translateEnabled ||
      !isNearViewport ||
      !bookId ||
      !server ||
      !enhanceKey
    ) {
      cancelEnhanceFetch()
      if (!enhanceEnabled || translateEnabled) {
        revokeEnhancedSrc()
        loadedEnhanceKey = null
      }
      enhancing = false
      return
    }

    if (loadedEnhanceKey === enhanceKey && enhancedSrc) {
      return
    }

    const cached = getCachedEnhancement(bookId, pageNum, false)
    if (cached) {
      applyEnhanceBlob(cached)
      enhancing = false
      return
    }

    cancelEnhanceFetch()
    enhancing = true

    const controller = new AbortController()
    enhanceAbort = controller

    try {
      const blob = await fetchEnhancement(server, bookId, pageNum, false, controller.signal)
      if (controller.signal.aborted) return
      if (!blob) {
        // bpp skip or failure — keep showing original
        return
      }
      applyEnhanceBlob(blob)
    } catch (err) {
      if ((err as Error)?.name === 'CanceledError' || (err as Error)?.name === 'AbortError') {
        return
      }
      // Keep original; no error toast (fail-open like API)
    } finally {
      if (enhanceAbort === controller) {
        enhancing = false
        enhanceAbort = null
      }
    }
  }

  $effect(() => {
    if (!translating) {
      stopEstimateTimer()
      return
    }
    startEstimateTimer()
    return () => stopEstimateTimer()
  })

  $effect(() => {
    if (!rootEl) return

    observer?.disconnect()
    observer = new IntersectionObserver(
      (entries) => {
        isNearViewport = entries.some((e) => e.isIntersecting)
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 },
    )
    observer.observe(rootEl)

    return () => {
      observer?.disconnect()
      observer = null
    }
  })

  $effect(() => {
    translateEnabled
    bookId
    pageNum
    isNearViewport

    if (!translateEnabled) {
      cancelTranslateFetch()
      revokeTranslatedSrc()
      loadedTranslateKey = null
      translating = false
      translateError = false
      return
    }

    if (translateKey && loadedTranslateKey === translateKey && translatedSrc) {
      return
    }

    void loadTranslation(false)

    return () => {
      cancelTranslateFetch()
    }
  })

  $effect(() => {
    const token = retranslateToken
    if (!token || token === lastHandledRetranslateToken) return
    if (!translateEnabled || !bookId || !server) {
      onRetranslateDone?.(pageNum, token)
      lastHandledRetranslateToken = token
      return
    }

    lastHandledRetranslateToken = token
    void (async () => {
      await loadTranslation(true)
      onRetranslateDone?.(pageNum, token)
    })()
  })

  $effect(() => {
    enhanceEnabled
    translateEnabled
    bookId
    pageNum
    isNearViewport

    if (!enhanceEnabled || translateEnabled) {
      cancelEnhanceFetch()
      revokeEnhancedSrc()
      loadedEnhanceKey = null
      enhancing = false
      return
    }

    if (enhanceKey && loadedEnhanceKey === enhanceKey && enhancedSrc) {
      return
    }

    void loadEnhancement()

    return () => {
      cancelEnhanceFetch()
    }
  })

  onDestroy(() => {
    observer?.disconnect()
    stopEstimateTimer()
    cancelTranslateFetch()
    cancelEnhanceFetch()
    revokeTranslatedSrc()
    revokeEnhancedSrc()
  })

  function handleLoad(e: Event) {
    onload?.(e)
  }

  function handleError() {
    onerror?.()
  }
</script>

<div bind:this={rootEl} class="relative mx-auto block" style={paddingStyle}>
  <img
    draggable="false"
    class="reader-page-img mx-auto block h-auto max-w-none"
    oncontextmenu={(e) => e.preventDefault()}
    src={displaySrc}
    alt="page {pageNum}"
    style={width ? `width: ${width}px;` : undefined}
    onload={handleLoad}
    onerror={handleError}
  />

  {#if enhanceEnabled && !translateEnabled && enhancing && !enhancedSrc}
    <div
      class="pointer-events-none absolute end-3 top-3 z-1"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        class="flex items-center gap-1.5 rounded-md border border-latte-surface1/70 bg-latte-mantle/90 px-2 py-1 shadow-sm backdrop-blur-md dark:border-macchiato-surface1/70 dark:bg-macchiato-mantle/90"
      >
        <SparklesIcon
          class="size-3.5 animate-pulse text-latte-mauve dark:text-macchiato-mauve"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span class="text-[10px] font-medium uppercase tracking-wide text-latte-text/70 dark:text-macchiato-text/70">
          Enhancing
        </span>
      </div>
    </div>
  {/if}

  {#if translateEnabled && translating}
    <div
      class="translate-overlay pointer-events-none absolute inset-0 flex items-center justify-center"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        class="translate-overlay__veil absolute inset-0 bg-latte-crust/35 backdrop-blur-[2px] dark:bg-macchiato-crust/45"
      ></div>

      <div
        class="translate-overlay__chip relative flex w-[min(100%,17rem)] flex-col gap-2.5 rounded-lg border border-latte-surface1/70 bg-latte-mantle/92 px-3.5 py-2.5 shadow-lg shadow-latte-crust/25 backdrop-blur-md dark:border-macchiato-surface1/70 dark:bg-macchiato-mantle/92 dark:shadow-macchiato-crust/40"
      >
        <div class="flex items-center gap-3">
          <div
            class="relative flex size-9 shrink-0 items-center justify-center rounded-md border border-latte-mauve/25 bg-latte-mauve/10 dark:border-macchiato-mauve/30 dark:bg-macchiato-mauve/10"
          >
            <span
              class="absolute inset-0 animate-ping rounded-md bg-latte-mauve/20 dark:bg-macchiato-mauve/25"
              aria-hidden="true"
            ></span>
            <LanguagesIcon
              class="relative size-4 text-latte-mauve dark:text-macchiato-mauve"
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </div>

          <div class="min-w-0 flex-1">
            <p
              class="text-[10px] font-medium uppercase tracking-wider text-latte-surface1 dark:text-macchiato-surface1"
            >
              translation
            </p>
            <div class="mt-0.5 flex items-center gap-2">
              <Spinner
                class="size-3.5 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
                strokeWidth={2.25}
              />
              <span class="text-sm font-medium text-latte-text dark:text-macchiato-text">
                {#if translateEstimateExceeded}
                  Taking a bit more time…
                {:else}
                  Translating page…
                {/if}
              </span>
            </div>
          </div>

          {#if !translateEstimateExceeded}
            <span
              class="shrink-0 tabular-nums text-xs font-medium text-latte-mauve dark:text-macchiato-mauve"
              aria-hidden="true"
            >
              ~{translateRemainingSec}s
            </span>
          {/if}
        </div>

        <div class="space-y-1">
          {#if translateEstimateExceeded}
            <div
              class="relative h-1 w-full overflow-hidden rounded-full bg-latte-surface1/50 dark:bg-macchiato-surface1/50"
              role="progressbar"
              aria-label="Translation is taking longer than expected"
            >
              <div
                class="translate-indeterminate-bar absolute inset-y-0 w-1/3 rounded-full bg-latte-mauve/70 dark:bg-macchiato-mauve/70"
              ></div>
            </div>
          {:else}
            <Progress
              value={estimateCountdownPct}
              max={100}
              class="h-1 bg-latte-surface1/50 dark:bg-macchiato-surface1/50 [&_[data-slot=progress-indicator]]:bg-latte-mauve dark:[&_[data-slot=progress-indicator]]:bg-macchiato-mauve [&_[data-slot=progress-indicator]]:transition-[transform] [&_[data-slot=progress-indicator]]:duration-200"
              aria-label="About {translateRemainingSec} seconds remaining"
            />
          {/if}
          <p class="text-[10px] text-latte-text/55 dark:text-macchiato-text/55">
            {#if translateEstimateExceeded}
              Still working — complex pages can take a little longer.
            {:else}
              Typical wait · up to {TRANSLATE_ESTIMATE_SEC}s
            {/if}
          </p>
        </div>
      </div>
    </div>
  {/if}

  {#if translateEnabled && translateError && !translating}
    <div
      class="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center px-3"
      role="alert"
    >
      <div
        class="flex max-w-[min(100%,18rem)] items-center gap-2 rounded-lg border border-latte-red/35 bg-latte-mantle/95 px-3 py-2 shadow-md backdrop-blur-md dark:border-macchiato-red/35 dark:bg-macchiato-mantle/95"
      >
        <AlertCircleIcon
          class="size-4 shrink-0 text-latte-red dark:text-macchiato-red"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span class="text-xs font-medium text-latte-red dark:text-macchiato-red">
          Translation failed
        </span>
      </div>
    </div>
  {/if}
</div>

<style>
  .translate-indeterminate-bar {
    animation: translate-indeterminate 1.8s ease-in-out infinite;
  }

  @keyframes translate-indeterminate {
    0% {
      left: -33%;
    }
    100% {
      left: 100%;
    }
  }
</style>
